import {
  PARTICIPANT_RATES,
  PRICE_MODES,
  PRICE_TYPES,
} from "../list/helpers/constants";

export const isPerService = (priceMode) =>
  priceMode === PRICE_MODES.perService;

/**
 * Precio a mostrar en home, listado y detalle.
 *
 * En `perService` las tarifas por pasajero llegan en `null`. `sanitisePrice`
 * devuelve el `null` tal cual y React no lo renderiza, así que leer
 * `adultPrice` sin mirar el modo deja el símbolo de moneda solo en pantalla.
 *
 * @param {Object} price objeto de precios (`price` o `convertedPrice`)
 * @param {string} priceMode
 * @returns {number|undefined}
 */
export const getDisplayPrice = (price, priceMode) =>
  isPerService(priceMode) ? price?.servicePrice : price?.adultPrice;

/**
 * Período que representa el precio, para el `t("byPeriods.per_${period}")` del
 * catálogo. `ticket` es el histórico y se traduce como "Por persona".
 *
 * @param {string} priceMode
 * @returns {string}
 */
export const getPricePeriod = (priceMode) =>
  isPerService(priceMode) ? PRICE_TYPES.service : "ticket";

/**
 * Colapsa el listing a una sola línea cuando la experiencia se cobra por
 * servicio.
 *
 * El back cobra una vez por línea de listing y no agrupa: `TransactionPricingV2`
 * instancia un `ExperiencePricing` por entrada y en `perService` cada uno
 * devuelve el `servicePrice` entero, sin multiplicar por `quantity`. Con
 * 1 adulto + 1 niño eso son dos líneas y se cobra dos veces la misma salida,
 * mientras la emisión de tickets agrupa bien y emite una sola.
 *
 * Los pasajeros no se pierden: viajan por el campo `passengers` del POST,
 * aparte del listing.
 *
 * @param {Array} listing líneas con `rate` y `quantity`
 * @param {string} priceMode
 * @returns {Array}
 */
export const collapseListingForPriceMode = (listing = [], priceMode) => {
  if (!isPerService(priceMode) || listing.length === 0) return listing;

  const totalQuantity = listing.reduce(
    (sum, line) => sum + (line.quantity || 0),
    0
  );

  return [
    { ...listing[0], rate: PRICE_TYPES.service, quantity: totalQuantity },
  ];
};

/**
 * Los contadores de pasajeros que se ofrecen al comprar.
 *
 * En `perService` no hay tarifa que elegir —la salida vale lo mismo vaya quien
 * vaya—, así que se cuenta gente y no categorías, igual que el dashboard. Los
 * datos de cada pasajero se siguen cargando en el checkout; lo que no se pide
 * es su categoría.
 *
 * Cuando sí hay tarifas se intersecan contra la lista de tipos de pasajero en
 * vez de derivar de las claves de `price`: `servicePrice` tiene valor y aparecía
 * como si "service" fuera una categoría de pasajero más.
 *
 * @param {Object} [experience]
 * @returns {string[]}
 */
export const getParticipantRates = (experience) => {
  if (isPerService(experience?.priceMode)) return [PRICE_TYPES.service];

  return PARTICIPANT_RATES.filter((rate) => {
    const price = experience?.price?.[`${rate}Price`];
    return price !== undefined && price !== null;
  });
};

/**
 * Las tarifas cargadas que esta experiencia no ofrece.
 *
 * El summary es global y sobrevive a la navegación entre experiencias, así que
 * los contadores de la anterior siguen ahí. Mandarlos igual no es cosmético: un
 * `service` sobre una experiencia `perPerson` hace que el back busque un
 * `servicePrice` que no existe y responda 500, y al revés infla la cantidad de
 * pasajeros de la reserva sin que se vea en pantalla.
 *
 * @param {Object} [tickets] contadores cargados, por tarifa
 * @param {string[]} [participantRates] las que la experiencia sí ofrece
 * @returns {string[]}
 */
export const getUnofferedTicketRates = (tickets = {}, participantRates = []) =>
  Object.keys(tickets).filter((rate) => !participantRates.includes(rate));

/**
 * El rate del titular: el pasajero que no se puede quitar de la reserva.
 *
 * @param {Object} [experience]
 * @returns {string}
 */
export const getMainParticipantRate = (experience) =>
  isPerService(experience?.priceMode) ? PRICE_TYPES.service : PRICE_TYPES.adult;
